import type { RefreshFailureReason } from '@/lib/api/api-client'
import type { ReactNode } from 'react'
import { createContext, useContext, useEffect, useRef, useState } from 'react'
import {
  getLastRefreshFailureReason,
  markInitialAuthCheckComplete,
  refreshAccessToken,
} from '@/lib/api/api-client'
import { authApi } from '@/lib/api/auth.api'
import { captureIdentityScope } from '@/lib/api/request-scope'
import {
  startTokenRefreshScheduler,
  stopTokenRefreshScheduler,
} from '@/lib/api/token-refresh-scheduler'
import { browserLoginScope } from '@/lib/auth/browser-login-scope'
import {
  isDeletedAccount,
  subscribeDeletedAccounts,
} from '@/lib/auth/deleted-account-state'
import { logoutSession } from '@/lib/auth/logout-session'
import { createLogger } from '@/lib/logger'
import { desktopAccountSession } from '@/lib/utils/native-credential-store'
import {
  clearWebViewCookies,
  desktopSecureStorage,
  isPopupWindow,
  isTauri,
  setupDeepLinkAuthListener,
} from '@/lib/utils/tauri'
import { useAuth as useAuthStore } from '@/store/auth.store'
import { resetPersistedModuleFilters } from '@/store/filter-scope'
import { clearTodayLayoutSnapshots } from '@/store/today-layout.store'
import {
  getAdminReturnTarget,
  redirectToAdminReturnTarget,
} from '@/utils/auth/admin-handoff'
import { AUTH_PUBLIC_PATHS } from '@/utils/auth/auth-constants'
import { getAuthRedirectTarget } from '@/utils/auth/redirects'
import { isMobileAuthEntry } from '@/utils/mobile-auth'
import { useQueryClient } from '@tanstack/react-query'
import { useLocation, useNavigate } from 'react-router-dom'

import type { AuthDataResponse, User } from '@scrambl/shared'
import type { SessionSnapshot } from '@scrambl/shared/session-scope'
import { SessionChangedError } from '@scrambl/shared/session-scope'

const logger = createLogger('components/auth/AuthProvider')

interface AuthContextProps {
  user: User | null
  isAuthenticated: boolean
  isLoading: boolean
  /**
   * The last auth check could not reach the API (restart, network blip,
   * 5xx/503). The session may still be valid, so protected routes show a
   * reconnecting state instead of treating this as a sign-out.
   */
  isAuthUnavailable: boolean
  setUser: (user: User | null) => void
  checkUserAuth: (options?: {
    force?: boolean
    debounceMs?: number
  }) => Promise<void>
  login: (email: string) => Promise<AuthDataResponse>
  logout: () => Promise<void>
  refreshToken: () => Promise<boolean>
}

const AuthContext = createContext<AuthContextProps | undefined>(undefined)

// Cache successful authentication paths to prevent redundant checks
// This addresses the issue of multiple checks on dashboard navigation
const AUTH_SUCCESS_CACHE = new Map<string, number>()
const AUTH_CACHE_DURATION = 60 * 1000 // 1 minute
const DEFAULT_AUTH_CHECK_DEBOUNCE = 200 // 200ms debounce for navigation events
let lastAuthCheckTimestamp = 0

// Module-level tracking for auth page checks to survive HMR and prevent loops
// Key: pathname, Value: timestamp of when the check was completed
const AUTH_PAGE_CHECK_COMPLETED = new Map<string, number>()
const AUTH_PAGE_CHECK_DURATION = 30 * 1000 // 30 seconds - don't re-check auth on same page within this window

// Module-level flag to track if an auth check is currently in flight
// This prevents multiple simultaneous auth checks across effects
let isAuthCheckInFlight = false

// Re-check auth after a network-level failure until the API answers. A dev
// server restart or deploy can take far longer than a few seconds, and giving
// up would strand the user on a reconnecting screen (or on /login without
// forwarding) even though the session is still valid. One GET /auth/me per
// interval is cheap; refreshes stay bounded by the api-client's own budget.
const TRANSIENT_AUTH_RETRY_BASE_MS = 1000
const TRANSIENT_AUTH_RETRY_MAX_MS = 15_000
let transientAuthRetryCount = 0

// Only these refresh outcomes prove the session is actually dead. Anything
// else (network failure, 5xx, no refresh attempted at all) may just be a
// transient outage and must never destroy a possibly-valid session.
function isDefinitiveAuthFailure(reason: RefreshFailureReason): boolean {
  return reason === 'unauthorized' || reason === 'missing_refresh_token'
}

// Check if we're on an auth page to skip unnecessary auth checks
function isAuthPage(pathname: string): boolean {
  return AUTH_PUBLIC_PATHS.some(authPath => {
    if (typeof authPath !== 'string') return false
    // For root path '/', use exact match to avoid matching all paths
    if (authPath === '/') return pathname === '/'
    // For other paths, use startsWith
    return pathname.startsWith(authPath)
  })
}

// Check if we're on a specific auth page that should redirect to dashboard when already authenticated
// This is more specific than isAuthPage and only includes login/register pages
function isRedirectAuthPage(pathname: string): boolean {
  const redirectPaths = [
    '/login',
    '/register',
    '/auth/callback',
    '/auth/google/callback',
    '/register/confirmation',
    '/login/confirmation',
  ]
  return redirectPaths.some(path => pathname.startsWith(path))
}

function isOAuthCallbackPage(pathname: string): boolean {
  return pathname === '/auth/callback' || pathname === '/auth/google/callback'
}

function isAddAccountFlow(search: string): boolean {
  const params = new URLSearchParams(search)
  return params.has('addAccount')
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient()
  const location = useLocation()
  const navigate = useNavigate()
  const [user, setUserState] = useState<User | null>(null)
  // Keep the credential epoch from the moment this user was accepted. A delayed
  // profile-sync effect must not attach it to a newly selected native account.
  const userCredentialSession = useRef<SessionSnapshot | null>(null)
  const userBrowserSession = useRef<SessionSnapshot | null>(null)
  const setUser = (nextUser: User | null) => {
    userCredentialSession.current = nextUser
      ? desktopAccountSession.capture()
      : null
    userBrowserSession.current =
      nextUser && !isTauri() ? browserLoginScope.capture() : null
    setUserState(nextUser)
  }
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [hasCheckedAuth, setHasCheckedAuth] = useState(false)
  const [authCheckCounter, setAuthCheckCounter] = useState(0)
  const pendingForcedCheckRef = useRef(false)
  const [isAuthUnavailable, setIsAuthUnavailable] = useState(false)
  const transientRetryTimerRef = useRef<number | null>(null)
  // Retries fire later; they must run the latest checkUserAuth so they see the
  // current route (e.g. /login?redirect=… after a manual navigation).
  const latestCheckUserAuthRef = useRef<
    | ((options?: { force?: boolean; debounceMs?: number }) => Promise<void>)
    | null
  >(null)

  // Track previous pathname for navigation logic
  const previousPath = useRef(location.pathname)

  const lastRedirectTarget = useRef<string | null>(null)

  useEffect(() => {
    if (!isAuthPage(location.pathname)) {
      lastRedirectTarget.current = null
    }
  }, [location.pathname])

  // Add a debounce timer ref
  const authCheckTimerRef = useRef<number | null>(null)

  // Track if desktop initialization (cookie cleanup) is complete
  // For web, this is always true immediately
  const [isDesktopReady, setIsDesktopReady] = useState(!isTauri())

  // On desktop app startup, clear stale WebView cookies if no valid tokens exist in secure storage
  // This prevents auth failures from stale cookies left over from previous sessions
  // For popup/focus windows, proactively authenticate using shared tokens
  useEffect(() => {
    if (!isTauri()) return // Web is always ready

    const initializeDesktop = async () => {
      try {
        // Set up deep link listener early to catch auth callbacks
        // This handles both active auth flows and "cold start" deep links
        await setupDeepLinkAuthListener()

        // Check if we have valid tokens in secure storage
        const hasValidTokens = await desktopSecureStorage.hasTokens()

        // For popup/focus windows, cookies are isolated from the main window
        // If we have tokens in shared storage, don't clear cookies - let auth flow use them
        if (!isPopupWindow() && !hasValidTokens) {
          await clearWebViewCookies()
        }
        // If tokens exist in main window, continue normally
      } catch (error) {
        logger.error('[AuthProvider] Desktop initialization error:', error)
      } finally {
        // Mark desktop as ready regardless of success/failure
        setIsDesktopReady(true)
      }
    }

    initializeDesktop()
  }, [])

  // Add a token refresh handler
  const handleRefreshToken = async (): Promise<boolean> => {
    const identity = captureIdentityScope()
    const browser = isTauri() ? null : browserLoginScope.capture()
    const desktopSession = isTauri() ? desktopAccountSession.capture() : null
    const assertCurrent = () => {
      identity.assertCurrent()
      browser?.assertCurrent()
      desktopSession?.assertCurrent()
    }
    try {
      const refreshSuccess = await refreshAccessToken()
      assertCurrent()

      if (refreshSuccess) {
        // After successful refresh, get the user data
        const userResponse = await authApi.getCurrentUser()
        assertCurrent()
        if (userResponse.success && userResponse.data) {
          setUser(userResponse.data)
          setIsAuthenticated(true)
          // Update auth store
          useAuthStore.setState(state => ({
            ...state,
            user: userResponse.data,
            isAuthenticated: true,
            isLoading: false,
            isAuthReady: true,
            lastAuthCheckTime: Date.now(),
          }))
          return true
        }
      }

      const refreshFailureReason = getLastRefreshFailureReason()
      if (!isDefinitiveAuthFailure(refreshFailureReason)) {
        logger.warn(
          '[AuthProvider] Refresh failure not definitive, preserving session',
          { reason: refreshFailureReason },
        )
        return false
      }

      // Clear desktop tokens/cookies only on definitive auth failures
      if (isTauri()) {
        stopTokenRefreshScheduler()
        await queryClient.cancelQueries()
        assertCurrent()
        await desktopSecureStorage.deleteTokens(desktopSession ?? undefined)
        await clearWebViewCookies()
      }
      return false
    } catch (error) {
      if (error instanceof SessionChangedError) throw error
      assertCurrent()
      logger.error('[AuthProvider] Token refresh error:', error)
      const refreshFailureReason = getLastRefreshFailureReason()
      if (!isDefinitiveAuthFailure(refreshFailureReason)) {
        logger.warn(
          '[AuthProvider] Refresh error not definitive, preserving session',
          { reason: refreshFailureReason },
        )
        return false
      }

      // Clear desktop tokens/cookies only on definitive auth failures
      if (isTauri()) {
        stopTokenRefreshScheduler()
        await queryClient.cancelQueries()
        assertCurrent()
        await desktopSecureStorage.deleteTokens(desktopSession ?? undefined)
        await clearWebViewCookies()
      }
      return false
    }
  }

  const clearTransientAuthRetry = () => {
    if (transientRetryTimerRef.current !== null) {
      window.clearTimeout(transientRetryTimerRef.current)
      transientRetryTimerRef.current = null
    }
  }

  // The check could not reach the API. Keep whatever auth state we have, flag
  // the outage (protected routes show a reconnecting state rather than
  // redirecting to /login) and re-check with capped backoff until it answers.
  const handleTransientAuthFailure = () => {
    setIsAuthUnavailable(true)
    setIsLoading(false)
    clearTransientAuthRetry()
    transientAuthRetryCount += 1
    const delay = Math.min(
      TRANSIENT_AUTH_RETRY_BASE_MS * Math.pow(2, transientAuthRetryCount - 1),
      TRANSIENT_AUTH_RETRY_MAX_MS,
    )
    logger.info(
      `[AuthProvider] Scheduling auth re-check in ${delay}ms (attempt ${transientAuthRetryCount})`,
    )
    transientRetryTimerRef.current = window.setTimeout(() => {
      transientRetryTimerRef.current = null
      void latestCheckUserAuthRef.current?.({ force: true, debounceMs: 0 })
    }, delay)
  }

  const markAuthCheckReachedServer = () => {
    transientAuthRetryCount = 0
    clearTransientAuthRetry()
    setIsAuthUnavailable(false)
  }

  // Function to check authentication status
  const checkUserAuth = async (options?: {
    force?: boolean
    debounceMs?: number
  }) => {
    // Wait for desktop initialization (cookie cleanup) before checking auth
    if (!isDesktopReady) {
      return
    }

    // Module-level guard to prevent multiple simultaneous auth checks
    // This check happens BEFORE the setTimeout to prevent scheduling multiple timers
    if (isAuthCheckInFlight) {
      if (options?.force) pendingForcedCheckRef.current = true
      return
    }

    // Get auth store state
    const { lastAuthCheckTime } = useAuthStore.getState()

    // Check if we're within cache period and not forcing refresh
    const now = Date.now()
    const isCacheValid =
      lastAuthCheckTime && now - lastAuthCheckTime < AUTH_CACHE_DURATION

    // Skip check if we're already authenticated and cache is valid
    if (isCacheValid && !options?.force && isAuthenticated && user) {
      return
    }

    // Skip redundant checks on auth pages
    if (isCacheValid && !options?.force && hasCheckedAuth) {
      // For auth pages with multiple checks, we can skip
      if (isAuthPage(location.pathname) && authCheckCounter > 1) {
        return
      }
    }

    // Clear any existing timer
    if (authCheckTimerRef.current) {
      window.clearTimeout(authCheckTimerRef.current)
      authCheckTimerRef.current = null
    }

    // Set module-level flag BEFORE scheduling the timer
    isAuthCheckInFlight = true

    // Set a small delay to debounce multiple calls
    return new Promise<void>(resolve => {
      authCheckTimerRef.current = window.setTimeout(async () => {
        authCheckTimerRef.current = null
        const identity = captureIdentityScope()
        const browser = isTauri() ? null : browserLoginScope.capture()
        const desktopSession = isTauri()
          ? desktopAccountSession.capture()
          : null
        const assertCurrent = () => {
          // A successful bootstrap can install its own first user. Existing
          // identities and native account selections must remain unchanged.
          if (identity.identity) identity.assertCurrent()
          browser?.assertCurrent()
          desktopSession?.assertCurrent()
        }
        try {
          // Update check counter
          setAuthCheckCounter(prev => prev + 1)

          setIsLoading(true)

          const response = await authApi.getCurrentUser()
          assertCurrent()

          if (response.success && response.data) {
            markAuthCheckReachedServer()
            setUser(response.data)
            setIsAuthenticated(true)

            // Record successful auth check time
            const currentTime = Date.now()

            // Update auth store
            useAuthStore.setState({
              user: response.data,
              isAuthenticated: true,
              isLoading: false,
              isAuthReady: true,
              lastAuthCheckTime: currentTime,
            })

            // If we're on a login page but we're already authenticated, check for redirect param
            // Only redirect if we're truly on an auth page (not admin pages)
            if (
              isRedirectAuthPage(location.pathname) &&
              !isAddAccountFlow(location.search) &&
              !isMobileAuthEntry(location.search) &&
              location.pathname !== '/today'
            ) {
              const adminRedirected = await redirectToAdminReturnTarget(
                getAdminReturnTarget(location.search),
              )
              if (adminRedirected) {
                resolve()
                return
              }

              const redirectTarget = getAuthRedirectTarget(
                location.search,
                location.pathname,
              )
              if (redirectTarget !== location.pathname + location.search) {
                if (lastRedirectTarget.current !== redirectTarget) {
                  lastRedirectTarget.current = redirectTarget
                  navigate(redirectTarget, { replace: true })
                }
              }
            }
          } else {
            // The check never reached the server (API restart, connection
            // blip). The session cookies may be perfectly valid; keep the
            // current auth state and let the next check settle it.
            if (response.networkError) {
              logger.warn(
                '[AuthProvider] Auth check failed at network level, preserving session',
              )
              handleTransientAuthFailure()
              resolve()
              return
            }

            // Browser refresh tokens live in HttpOnly cookies, so JavaScript
            // cannot inspect them. Let the API decide whether one exists.
            const hasDesktopTokens = isTauri()
              ? await desktopSecureStorage.hasTokens()
              : false
            assertCurrent()
            const canAttemptRefresh = isTauri() ? hasDesktopTokens : true

            // Try token refresh on auth pages and protected pages. Refresh
            // attempts are bounded by the api-client's refresh budget; a
            // counter here would stop refreshing after a few transient
            // re-checks and misreport a live session as signed out.
            if (canAttemptRefresh) {
              const refreshResult = await handleRefreshToken()
              assertCurrent()

              if (refreshResult) {
                markAuthCheckReachedServer()
                // Token refresh was successful and user data was updated
                // If on auth page, redirect to organizations
                if (
                  isRedirectAuthPage(location.pathname) &&
                  !isAddAccountFlow(location.search) &&
                  !isMobileAuthEntry(location.search)
                ) {
                  const adminRedirected = await redirectToAdminReturnTarget(
                    getAdminReturnTarget(location.search),
                  )
                  if (adminRedirected) {
                    resolve()
                    return
                  }

                  const redirectTarget = getAuthRedirectTarget(
                    location.search,
                    location.pathname,
                  )
                  if (redirectTarget !== location.pathname + location.search) {
                    if (lastRedirectTarget.current !== redirectTarget) {
                      lastRedirectTarget.current = redirectTarget
                      navigate(redirectTarget, { replace: true })
                    }
                  }
                }
                resolve()
                return
              }

              const refreshFailureReason = getLastRefreshFailureReason()
              if (!isDefinitiveAuthFailure(refreshFailureReason)) {
                logger.warn(
                  '[AuthProvider] Refresh failure not definitive, preserving auth state',
                  { reason: refreshFailureReason },
                )
                handleTransientAuthFailure()
                resolve()
                return
              }
            }

            // If no refresh token or refresh failed, handle as unauthenticated
            markAuthCheckReachedServer()
            setUser(null)
            setIsAuthenticated(false)

            // Update auth store to reflect unauthenticated state
            useAuthStore.setState({
              user: null,
              isAuthenticated: false,
              isLoading: false,
              isAuthReady: true,
              lastAuthCheckTime: Date.now(),
            })

            if (isTauri()) {
              await clearWebViewCookies()
            }

            // If we're on a protected page but not authenticated, redirect to login
            if (!isAuthPage(location.pathname)) {
              const returnPath = encodeURIComponent(
                location.pathname + location.search,
              )
              navigate(`/login?redirect=${returnPath}`)
            }
          }
        } catch (error) {
          if (error instanceof SessionChangedError) {
            resolve()
            return
          }
          // A transport failure can also arrive after the login was replaced.
          // Never classify that older failure against the new session.
          try {
            assertCurrent()
          } catch (changed) {
            if (changed instanceof SessionChangedError) return
            throw changed
          }
          const refreshFailureReason = getLastRefreshFailureReason()
          if (!isDefinitiveAuthFailure(refreshFailureReason)) {
            logger.warn(
              '[AuthProvider] Auth check error without definitive auth failure, preserving auth state',
              { reason: refreshFailureReason, error },
            )
            setHasCheckedAuth(true)
            handleTransientAuthFailure()
            resolve()
            return
          }

          markAuthCheckReachedServer()
          setUser(null)
          setIsAuthenticated(false)

          // Update auth store to reflect error state
          useAuthStore.setState({
            user: null,
            isAuthenticated: false,
            isLoading: false,
            isAuthReady: true,
            lastAuthCheckTime: Date.now(),
          })

          if (isTauri()) {
            await clearWebViewCookies()
          }

          // If we're on a protected page but not authenticated, redirect to login
          if (!isAuthPage(location.pathname)) {
            const returnPath = encodeURIComponent(
              location.pathname + location.search,
            )
            navigate(`/login?redirect=${returnPath}`)
          }
        } finally {
          let currentLogin = true
          try {
            browser?.assertCurrent()
          } catch {
            currentLogin = false
          }
          if (currentLogin) {
            setIsLoading(false)
            setHasCheckedAuth(true)
            markInitialAuthCheckComplete()
          }
          // Reset module-level flag
          isAuthCheckInFlight = false
          if (pendingForcedCheckRef.current) {
            pendingForcedCheckRef.current = false
            window.setTimeout(() => {
              void latestCheckUserAuthRef.current?.({
                force: true,
                debounceMs: 0,
              })
            }, 0)
          }
          resolve()
        }
      }, options?.debounceMs ?? DEFAULT_AUTH_CHECK_DEBOUNCE)
    })
  }

  useEffect(() => {
    latestCheckUserAuthRef.current = checkUserAuth
  })

  useEffect(() => {
    if (isTauri()) return
    return browserLoginScope.subscribe(() => {
      // Fresh credentials require new profile/membership resolution, including
      // a login to the same account. React's old copy cannot repopulate the store.
      userCredentialSession.current = null
      setUserState(null)
      setIsAuthenticated(false)
      setHasCheckedAuth(false)
      setIsLoading(true)
      if (useAuthStore.getState().user)
        useAuthStore.setState({
          user: null,
          isAuthenticated: false,
          isAuthReady: false,
          lastAuthCheckTime: null,
        })
      clearTransientAuthRetry()
      AUTH_SUCCESS_CACHE.clear()
      AUTH_PAGE_CHECK_COMPLETED.clear()
      lastAuthCheckTimestamp = 0
      queryClient.clear()
      void latestCheckUserAuthRef.current?.({ force: true, debounceMs: 0 })
    })
  }, [queryClient])

  // While the API is unreachable, re-check as soon as the browser reports it
  // is back online or the tab becomes visible again, rather than waiting out
  // the backoff.
  useEffect(() => {
    if (!isAuthUnavailable) return
    const recheckNow = () => {
      if (document.visibilityState === 'hidden') return
      transientAuthRetryCount = 0
      clearTransientAuthRetry()
      void latestCheckUserAuthRef.current?.({ force: true, debounceMs: 0 })
    }
    window.addEventListener('online', recheckNow)
    document.addEventListener('visibilitychange', recheckNow)
    return () => {
      window.removeEventListener('online', recheckNow)
      document.removeEventListener('visibilitychange', recheckNow)
    }
  }, [isAuthUnavailable])

  // On unmount (including HMR remounts) drop scheduled work. A debounced
  // check that never runs would otherwise leave the module-level in-flight
  // flag set and silently block every later auth check.
  useEffect(
    () => () => {
      latestCheckUserAuthRef.current = null
      pendingForcedCheckRef.current = false
      clearTransientAuthRetry()
      if (authCheckTimerRef.current !== null) {
        window.clearTimeout(authCheckTimerRef.current)
        authCheckTimerRef.current = null
        isAuthCheckInFlight = false
      }
    },
    [],
  )

  // Update the effect that handles auth_success parameter
  useEffect(() => {
    // Wait for desktop to be ready before handling auth success
    if (!isDesktopReady) return

    const searchParams = new URLSearchParams(location.search)
    const needsRedirect = searchParams.get('auth_success')
    const wasPrefetched = searchParams.get('prefetch') === 'true'

    if (needsRedirect === 'true') {
      // Skip the check if we already have user data and are authenticated
      if (user && isAuthenticated) {
        return
      }

      // Use a shorter debounce time for prefetched organizations
      const debounceTime = wasPrefetched ? 200 : 500

      try {
        // If orgs were prefetched during login, we can do a lighter auth check
        if (wasPrefetched) {
          checkUserAuth({ debounceMs: debounceTime })
        } else {
          // Force auth check when auth_success is present without prefetch (bypassing cache)
          checkUserAuth({ force: true, debounceMs: debounceTime })
        }
      } catch (e) {
        logger.error('[AuthProvider] Error during auth check:', e)
      }
    }
    // Depend on actual props, not function closure that's fresh every render
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, user, isAuthenticated, isDesktopReady])

  // On mount or route change, check if the user is already authenticated
  useEffect(() => {
    // Wait for desktop to be ready before checking auth
    if (!isDesktopReady) {
      return
    }

    const now = Date.now()
    const isOnAuthPage = isAuthPage(location.pathname)

    if (isOAuthCallbackPage(location.pathname)) {
      if (isLoading) {
        setIsLoading(false)
      }
      setHasCheckedAuth(true)
      markInitialAuthCheckComplete()
      return
    }

    // For auth pages, use module-level cache to prevent loops (survives HMR)
    if (isOnAuthPage) {
      if (isAuthenticated && user) {
        if (isLoading) {
          setIsLoading(false)
        }
        setHasCheckedAuth(true)
        // Only redirect on login/register pages, NOT on public pages like /f/ (forms)
        if (
          isRedirectAuthPage(location.pathname) &&
          !isAddAccountFlow(location.search) &&
          !isMobileAuthEntry(location.search)
        ) {
          void (async () => {
            const adminRedirected = await redirectToAdminReturnTarget(
              getAdminReturnTarget(location.search),
            )
            if (adminRedirected) {
              return
            }

            const redirectTarget = getAuthRedirectTarget(
              location.search,
              location.pathname,
            )
            if (redirectTarget !== location.pathname + location.search) {
              if (lastRedirectTarget.current !== redirectTarget) {
                lastRedirectTarget.current = redirectTarget
                navigate(redirectTarget, { replace: true })
              }
            }
          })()
        }
        return
      }

      const lastAuthPageCheck = AUTH_PAGE_CHECK_COMPLETED.get(location.pathname)
      const hasRecentlyChecked =
        lastAuthPageCheck && now - lastAuthPageCheck < AUTH_PAGE_CHECK_DURATION

      if (hasRecentlyChecked) {
        // Ensure loading state is correct
        if (isLoading) {
          setIsLoading(false)
          setHasCheckedAuth(true)
        }
        return
      }

      // Mark that we're checking this auth page
      AUTH_PAGE_CHECK_COMPLETED.set(location.pathname, now)
      lastAuthCheckTimestamp = now
      checkUserAuth({ force: true, debounceMs: 0 })
      return
    }

    // Non-auth pages: use the existing logic
    // Prevent duplicate checks within a short time window (debounce)
    const timeSinceLastCheck = now - lastAuthCheckTimestamp

    if (timeSinceLastCheck < DEFAULT_AUTH_CHECK_DEBOUNCE) {
      return
    }

    // Check if this path was recently authenticated
    const cachedTime = AUTH_SUCCESS_CACHE.get(location.pathname)
    const isCacheValid = cachedTime && now - cachedTime < AUTH_CACHE_DURATION

    if (isCacheValid && isAuthenticated) {
      return
    }

    // Only check on first load or significant navigation
    const isFirstLoad = !hasCheckedAuth
    const isNavigatingFromAuthToNonAuth =
      isAuthPage(previousPath.current) && !isAuthPage(location.pathname)

    const shouldCheckAuth = isFirstLoad || isNavigatingFromAuthToNonAuth

    if (shouldCheckAuth) {
      lastAuthCheckTimestamp = now
      checkUserAuth()

      // If authenticated, cache this path as successfully authenticated
      if (isAuthenticated) {
        AUTH_SUCCESS_CACHE.set(location.pathname, now)
      }
    }

    // Track previous path for future navigation logic
    previousPath.current = location.pathname
    // Depend on actual props, not function closure that's fresh every render
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    hasCheckedAuth,
    location.pathname,
    location.search,
    isAuthenticated,
    isDesktopReady,
    isLoading,
    navigate,
    user,
  ])

  useEffect(() => {
    const retire = () => {
      if (!user || !isDeletedAccount(user.id)) return
      userCredentialSession.current = null
      userBrowserSession.current = null
      setUserState(null)
      setIsAuthenticated(false)
      setIsLoading(false)
      setHasCheckedAuth(true)
    }
    retire()
    return subscribeDeletedAccounts(retire)
  }, [user])

  // Sync state with the auth store
  useEffect(() => {
    if (user && userBrowserSession.current) {
      try {
        userBrowserSession.current.assertCurrent()
      } catch (error) {
        if (!(error instanceof SessionChangedError)) throw error
        setUser(null)
        setIsAuthenticated(false)
        return
      }
    }
    const authState = useAuthStore.getState()

    // If we have user data in the store but not in local state, sync them
    if (!user && authState.user && authState.isAuthenticated) {
      setUser(authState.user)
      setIsAuthenticated(true)
      setIsLoading(false)
      setHasCheckedAuth(true)
    }

    // If we have user data in local state but store doesn't, update store
    if (
      user &&
      !isDeletedAccount(user.id) &&
      !authState.user &&
      isAuthenticated
    ) {
      useAuthStore.setState({
        user,
        isAuthenticated: true,
        isLoading: false,
        isAuthReady: true,
      })
    }
  }, [user, isAuthenticated])

  // Keep tokens fresh proactively while authenticated (prevents 401 bursts
  // after sleep/idle and keeps the sliding refresh cookie extending)
  useEffect(() => {
    if (!isAuthenticated) return
    startTokenRefreshScheduler()
    return () => stopTokenRefreshScheduler()
  }, [isAuthenticated])

  const reactionOwner = useRef<string | null>(null)
  // After user is successfully authenticated, store the user ID for reaction tracking
  useEffect(() => {
    if (user?.id) {
      if (isDeletedAccount(user.id)) return
      reactionOwner.current = user.id
      const previousUserId = localStorage.getItem('currentUserId')
      if (previousUserId && previousUserId !== user.id) {
        resetPersistedModuleFilters()
        clearTodayLayoutSnapshots()
        localStorage.removeItem('currentOrganizationId')
      }
      // Store current user ID for cross-tab recognition with reactions
      localStorage.setItem('currentUserId', user.id)
    } else if (
      reactionOwner.current &&
      localStorage.getItem('currentUserId') === reactionOwner.current
    ) {
      localStorage.removeItem('currentUserId')
    }
  }, [user?.id])

  useEffect(() => {
    if (!user || !isTauri()) return
    const session = userCredentialSession.current
    if (!session || session.signal.aborted) return
    desktopSecureStorage.upsertAccountProfile(user, session).catch(error => {
      logger.error(
        '[AuthProvider] Failed to sync desktop account profile:',
        error,
      )
    })
  }, [user?.id])

  // Login handler
  const handleLogin = (email: string) => {
    return authApi.login(email)
  }

  // Logout handler
  const handleLogout = async () => {
    const completion = await logoutSession()
    completion.assertCurrent()
    resetPersistedModuleFilters()
    clearTodayLayoutSnapshots()
    setUser(null)
    setIsAuthenticated(false)
    useAuthStore.setState({
      user: null,
      isAuthenticated: false,
      isLoading: false,
      isAuthReady: true,
      lastAuthCheckTime: Date.now(),
    })
    queryClient.clear()
    navigate(completion.response.redirect ?? '/login')
  }

  // Update user data
  const handleSetUser = (newUser: User | null) => {
    setUser(newUser)
    setIsAuthenticated(!!newUser)
    if (newUser) markAuthCheckReachedServer()

    // Reset auth check counter when user changes
    setAuthCheckCounter(0)

    // Update auth store
    useAuthStore.setState({
      user: newUser,
      isAuthenticated: !!newUser,
      isLoading: false,
      isAuthReady: true,
      lastAuthCheckTime: Date.now(),
    })
  }

  const value = {
    user,
    isAuthenticated,
    isLoading,
    isAuthUnavailable,
    setUser: handleSetUser,
    checkUserAuth,
    login: handleLogin,
    logout: handleLogout,
    refreshToken: handleRefreshToken,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
